import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  MoreVertical,
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Edit2,
  Trash2,
  Mail,
  Phone,
  Loader2,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { ConfirmDialog } from "../Menu_Management/UIComponents";
import useEmployee from "@/hooks/useEmployee";
import Pagination from "@/components/shared/Pagination";
import { useAuth } from "@/context/AuthContext";
import { P } from "@/config/permissionCodes";

export default function EmployeeView({ renderActions }) {
  const { hasPermission } = useAuth();
  const canViewEmployees = hasPermission(P.HRM.EMPLOYEES.SCREEN);
  const canEditEmployee = hasPermission(P.HRM.EMPLOYEES.EDIT);
  const canDeleteEmployee = hasPermission(P.HRM.EMPLOYEES.DELETE);
  if (!canViewEmployees) {
  return (
    <div className="flex items-center justify-center h-full py-24">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          Access Denied
        </h3>

        <p className="text-sm text-slate-500">
          You don't have permission to view this page.
        </p>
      </div>
    </div>
  );
}
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [filterText, setFilterText] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    type: "",
    data: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const navigate = useNavigate();
  const location = useLocation();
  const actionMenuRef = useRef(null);

  const { employees, deleteEmployee, fetchEmployees, loading } = useEmployee();

  const handleAddNew = () => {
    navigate("/hrm/employees/add");
  };

  // Determine base path for navigation
  const isHRMRoute = location.pathname.startsWith('/hrm');
  const basePath = isHRMRoute ? '/hrm/employees' : '/admin/employee';

  const showToast = (message, type = "success") => {
    if (type === "success") toast.success(message);
    else if (type === "error") toast.error(message);
    else toast(message);
  };

  const handleRefresh = async () => {
    await fetchEmployees();
    showToast("Employees refreshed", "success");
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openActionMenu && actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
        setOpenActionMenu(null);
        setSelectedEmployee(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openActionMenu]);

  useEffect(() => {
   renderActions({
  onRefresh: fetchEmployees,
  onAddNew: handleAddNew
});
  }, []);

  /* FILTERS */
  const roleList = useMemo(() => {
    const set = new Set();
    employees?.forEach((e) => e.role && set.add(e.role));
    return ["all", ...Array.from(set)];
  }, [employees]);

  const filteredEmployees = useMemo(() => {
    return employees.filter((emp) => {
      const text = filterText.toLowerCase();
      const matchesText = [emp.name, emp.email, emp.phone, emp.role]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(text));
      const matchesRole = filterRole === "all" || emp.role === filterRole;
      return matchesText && matchesRole;
    });
  }, [employees, filterText, filterRole]);

  /* SORTING */
  const sortedEmployees = useMemo(() => {
    let list = [...filteredEmployees];
    if (!sortConfig.key) return list;
    list.sort((a, b) => {
      const A = (a[sortConfig.key] || "").toLowerCase();
      const B = (b[sortConfig.key] || "").toLowerCase();
      if (A < B) return sortConfig.direction === "asc" ? -1 : 1;
      if (A > B) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [filteredEmployees, sortConfig]);

  /* PAGINATION */
  const totalItems = sortedEmployees.length;
  const totalPages = totalItems === 0 ? 1 : Math.ceil(totalItems / pageSize);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [totalItems]);

  const paginatedEmployees = useMemo(() => {
    if (loading) return [];
    const startIndex = (currentPage - 1) * pageSize;
    return sortedEmployees.slice(startIndex, startIndex + pageSize);
  }, [sortedEmployees, currentPage, pageSize, loading]);

  /* DELETE */
  const deleteNow = async () => {
    try {
      await deleteEmployee(confirmDialog.data);
      showToast("Employee deleted", "success");
    } catch {
      showToast("Delete failed", "error");
    }
    setConfirmDialog({ isOpen: false, type: "", data: null });
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown size={12} className="text-gray-400" />;
    return sortConfig.direction === "asc"
      ? <ArrowUp size={12} className="text-teal-600" />
      : <ArrowDown size={12} className="text-teal-600" />;
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg">
          <Search size={16} className="text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="flex-1 bg-transparent outline-none text-sm text-gray-700 placeholder-gray-400"
          />
        </div>
        <select
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
          className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 outline-none focus:ring-1 focus:ring-teal-500"
        >
          <option value="all">All Roles</option>
          {roleList.filter(r => r !== "all").map((role) => (
            <option key={role} value={role}>{role}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50 border-b border-gray-200">
              <th
                onClick={() => handleSort("name")}
                className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100"
              >
                <div className="flex items-center gap-1.5">
                  Employee <SortIcon columnKey="name" />
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                Contact
              </th>
              <th
                onClick={() => handleSort("role")}
                className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100"
              >
                <div className="flex items-center gap-1.5">
                  Role <SortIcon columnKey="role" />
                </div>
              </th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wide w-20">
                Actions
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={4} className="py-12 text-center">
                  <Loader2 className="w-6 h-6 animate-spin text-teal-600 mx-auto" />
                </td>
              </tr>
            ) : paginatedEmployees.length === 0 ? (
              <tr>
                <td colSpan={4} className="py-12 text-center text-gray-500 text-sm">
                  No employees found
                </td>
              </tr>
            ) : (
              paginatedEmployees.map((emp) => (
                <tr key={emp.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center text-white text-xs font-semibold">
                        {emp.name?.substring(0, 2)?.toUpperCase()}
                      </div>
                      <div>
                        <button
                          onClick={() => navigate(`${basePath}/view/${emp.id}`)}
                          className="font-medium text-gray-900 hover:text-teal-600 hover:underline text-left"
                        >
                          {emp.name}
                        </button>
                        <p className="text-xs text-gray-500">{emp.role || "—"}</p>
                      </div>
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <div className="space-y-1">
                      <div className="flex items-center gap-1.5 text-sm text-gray-600">
                        <Mail size={12} className="text-gray-400" />
                        <span className="truncate max-w-[200px]">{emp.email || "—"}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-sm text-gray-600">
                        <Phone size={12} className="text-gray-400" />
                        {emp.phone || "—"}
                      </div>
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <span className="inline-flex px-2 py-1 text-xs font-medium bg-slate-100 text-slate-700 rounded">
                      {emp.role || "—"}
                    </span>
                  </td>

                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setDropdownPos({
                          top: rect.bottom + window.scrollY + 4,
                          left: rect.right - 160 + window.scrollX,
                        });
                        setSelectedEmployee(emp);
                        setOpenActionMenu(openActionMenu === emp.id ? null : emp.id);
                      }}
                      className="p-1.5 hover:bg-gray-100 rounded-md text-gray-500 hover:text-gray-700"
                    >
                      <MoreVertical size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {!loading && sortedEmployees.length > 0 && (
          <div className="border-t border-gray-200 bg-slate-50 px-4 py-3">
            <Pagination
              totalPages={totalPages}
              currentPage={currentPage}
              onPageChange={(page) => setCurrentPage(page)}
              totalItems={totalItems}
              pageSize={pageSize}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setCurrentPage(1);
              }}
              showPageSizeSelector={true}
              pageSizeOptions={[5, 10, 20, 50]}
            />
          </div>
        )}
      </div>

      {/* Action Dropdown */}
      {openActionMenu && selectedEmployee && (
        <div
          ref={actionMenuRef}
          className="fixed bg-white shadow-lg border border-gray-200 rounded-lg py-1 w-40 z-[9999]"
          style={{ top: dropdownPos.top, left: dropdownPos.left }}
        >
          <button
            onClick={() => {
              navigate(`${basePath}/edit/${selectedEmployee.id}`);
              setOpenActionMenu(null);
            }}
            className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-sm text-gray-700 w-full"
          >
            <Edit2 size={14} />
            Edit
          </button>
          <button
            onClick={() => {
              setConfirmDialog({
                isOpen: true,
                type: "delete",
                data: selectedEmployee.id,
              });
              setOpenActionMenu(null);
            }}
            className="flex items-center gap-2 px-3 py-2 hover:bg-red-50 text-sm text-red-600 w-full"
          >
            <Trash2 size={14} />
            Delete
          </button>
        </div>
      )}

      {/* Delete Dialog */}
      {confirmDialog.isOpen && (
        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          title="Delete Employee"
          message="Are you sure you want to delete this employee? This action cannot be undone."
          confirmText="Delete"
          type="danger"
          onClose={() => setConfirmDialog({ isOpen: false, type: "", data: null })}
          onConfirm={deleteNow}
        />
      )}
    </div>
  );
}
